import { ACTIVITY_KIND_LABEL, fmt, type CalcResult, type Scenario } from "@h2lca/core";
import { Button } from "@/components/ui/FormControls";
import { useWorkshop } from "@/store/workshop";
import { StageBadge } from "../Badges";
import { FlowDiagram } from "../FlowDiagram";
import { GlossaryTip } from "../GlossaryTip";
import { Block, Section } from "../Section";

/** ③ 설비 구성 , 공정 흐름도 + 선택 설비의 투입,산출과 탄소배출량 계산 */
export function EquipmentStep({ scenario, result }: { scenario: Scenario; result: CalcResult }) {
  const selectedId = useWorkshop((s) => s.selectedEquipmentId);
  const setStep = useWorkshop((s) => s.setStep);
  const eq = scenario.equipment.find((e) => e.id === selectedId) ?? null;
  const r = eq ? result.byEquipment.find((x) => x.equipmentId === eq.id) : undefined;
  const inCount = result.byEquipment.filter((e) => e.inBoundary).length;

  return (
    <Section
      title={
        <>
          설비 구성 <GlossaryTip k="unit_process" />
        </>
      }
      desc="설비 팔레트에서 설비를 선택해 추가하고, 카드의 순서 이동 버튼으로 공정 순서를 조정합니다. 각 설비는 하나의 단위공정입니다."
      aside={
        <span className="text-footnote text-ink-subtle">
          설비 <b className="font-mono text-ink">{scenario.equipment.length}</b>, 경계 안 <b className="font-mono text-ink">{inCount}</b>
        </span>
      }
    >
      <FlowDiagram equipment={scenario.equipment} result={result} />

      <div className="ws-paired-panels">
        <Block title="투입 및 산출자료">
          {!eq ? (
            <p className="text-footnote text-ink-subtle">설비 카드를 누르면 투입 및 산출자료가 보입니다.</p>
          ) : (
            <>
              <p className="flex items-center gap-inline text-caption font-semibold text-ink">
                <StageBadge stage={eq.stage} /> {eq.name}
              </p>
              <div className="table-scroll"><table className="ws-table">
                <thead>
                  <tr>
                    <th>구분</th>
                    <th>항목</th>
                    <th className="num">수량</th>
                    <th>단위</th>
                  </tr>
                </thead>
                <tbody>
                  {eq.inputs.map((i) => (
                    <tr key={i.id} data-muted={i.kind === "parameter"}>
                      <td>{i.kind === "parameter" ? "참고" : "투입"}</td>
                      <td>
                        {i.label} <span className="text-micro text-ink-subtle">({ACTIVITY_KIND_LABEL[i.kind]})</span>
                      </td>
                      <td className="num">{i.amount === null ? "없음" : fmt(i.amount)}</td>
                      <td>{i.unit}</td>
                    </tr>
                  ))}
                  {eq.h2.role !== "none" && (
                    <>
                      {eq.h2.role === "passthrough" && (
                        <tr>
                          <td>투입</td>
                          <td>수소</td>
                          <td className="num">{eq.h2.inKg === null ? "없음" : fmt(eq.h2.inKg)}</td>
                          <td>kg</td>
                        </tr>
                      )}
                      <tr>
                        <td>산출</td>
                        <td>수소{eq.h2.role === "producer" ? "(생산)" : ""}</td>
                          <td className="num">{eq.h2.outKg === null ? "없음" : fmt(eq.h2.outKg)}</td>
                        <td>kg</td>
                      </tr>
                      {eq.h2.role === "passthrough" && (
                        <tr data-muted="true">
                          <td>손실</td>
                          <td>수소 손실</td>
                          <td className="num">{eq.h2.lossKg === null ? "없음" : fmt(eq.h2.lossKg)}</td>
                          <td>kg</td>
                        </tr>
                      )}
                    </>
                  )}
                  {eq.inputs.length === 0 && eq.h2.role === "none" && (
                    <tr>
                      <td colSpan={4} className="text-ink-subtle">
                        입력 항목이 없습니다. 운전자료 입력 단계에서 추가합니다.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table></div>
              <div><Button onClick={() => setStep("inputs")}>
                운전자료 입력으로
              </Button></div>
            </>
          )}
        </Block>

        <Block title="탄소배출량 계산">
          {!eq || !r ? (
            <p className="text-footnote text-ink-subtle">설비를 선택하면 사용량 × 배출계수 계산이 보입니다.</p>
          ) : !r.inBoundary ? (
            <p className="text-footnote text-ink-subtle">시스템 경계 밖 설비라 계산에서 제외됩니다.</p>
          ) : (
            <>
              <ul className="flex flex-col gap-label font-mono text-footnote text-ink-soft">
                {r.lines.map((l) => (
                  <li key={l.inputId} className={l.excluded ? "text-ink-subtle" : ""}>
                    <span className="font-sans text-ink-muted">{l.label}: </span>
                    {l.formula}
                  </li>
                ))}
                {r.lines.length === 0 && <li className="text-ink-subtle">입력 항목 없음</li>}
              </ul>
              <p className="border-t border-line-soft pt-item text-caption text-ink">
                소계 <b className="font-mono text-figure">{fmt(r.kgCO2eq)}</b> <span className="text-micro text-ink-muted">kgCO2eq</span>
                {result.totalKgCO2eq > 0 && <span className="ml-item font-mono text-ink-muted">({(r.share * 100).toFixed(1)}%)</span>}
              </p>
              <p className="text-footnote text-ink-subtle">용수와 기타 투입자료는 LCI에 기록하되, 배출계수가 없는 항목은 계산에서 제외합니다.</p>
            </>
          )}
        </Block>
      </div>
    </Section>
  );
}
